import { Flags } from '@fullstory/client-api';
import {
  ComputeMetricResponse as AlexaComputeMetricResponse,
  MetricsServicePublic as LegacyMetricsServicePublic,
  QueryCacheOptions,
} from '@fullstory/proto/fs/services/alexa';
import {
  AppBackendPublic,
  ArchiveMetricRequest,
  ComputeMetricRequest,
  ComputeMetricResponse,
  CreateUnnamedMetricRequest,
  GetUnnamedMetricRequest,
  MarkMetricViewedRequest,
  MetricsPublic,
  SetMetricRequest,
  StarMetricRequest,
} from '@fullstory/proto/fs/services/dx-objects';
import { CanAccessRequest } from '@fullstory/proto/fs/shared/accesscontrol';
import { Timestamp, timestamp } from '@fullstory/proto/google/protobuf';
import {
  createRequestCancellationAction,
  RequestDispatch,
  createGRPCAction,
  RequestActionType,
} from '@fullstory/redux-util';
import { defaultCacheKey } from '@fullstory/requests';
import { createAppCacheActions } from '@fullstory/redux-util/utils/appCache';
import { createAppCacheRequestAction } from '@fullstory/redux-util/utils/appCache/actions';
import { ActionType } from '@fullstory/redux-util/utils/types';
import { equalArrays, isDataFresh } from '@fullstory/searchiecache';
import { isNonNull } from '@fullstory/typeutils';
import { hashObject } from '@fullstory/util/hash';
import { UID } from '@fullstory/util/uid';
import { BatchOptions } from '@fullstory/util/xhr';
import jstz from 'jstz';
import { AppDispatch } from '../../types';
import { SearchCacheKey } from '../searchMeta';
import {
  ComputeMetricResponseWithId,
  LegacyMetricsRequestBody,
  MetricsRequestBody,
  MetricsRequestTypes,
} from './types';
import { metricsRequestBodyToLegacyComputeMetricRequest } from './utils';

const DX_OBJECTS_GET_ALL_METRICS_ENDPOINT = MetricsPublic.GetAllMetrics(
  () => new Promise(_ => {}),
  {
    orgId: '',
    includeArchived: false,
    pageInfo: null,
  },
).endpoint;

export const metricCacheKeysToInvalidate = (orgId: string) => [
  defaultCacheKey(DX_OBJECTS_GET_ALL_METRICS_ENDPOINT, {
    includeArchived: true,
    orgId,
  }),
  defaultCacheKey(DX_OBJECTS_GET_ALL_METRICS_ENDPOINT, {
    includeArchived: false,
    orgId,
  }),
];

export const MetricsRequestActions = {
  getMetric: (orgId: string, metricId: string, force?: boolean) =>
    createGRPCAction<typeof MetricsPublic.GetMetric>()(
      {
        type: MetricsRequestTypes.GetMetricRequest,
        grpcFunction: MetricsPublic.GetMetric,
        body: { orgId, metricId },
        force,
        readWrite: 'read',
      },
      MetricsRequestTypes.GetMetricSuccess,
      MetricsRequestTypes.GetMetricFailure,
    ),
  getAllMetrics: (orgId: string, force?: boolean) =>
    createGRPCAction<typeof MetricsPublic.GetAllMetrics>()(
      {
        type: MetricsRequestTypes.GetAllMetricsRequest,
        grpcFunction: MetricsPublic.GetAllMetrics,
        body: { orgId, includeArchived: false, pageInfo: null },
        force,
        readWrite: 'read',
        settings: {
          headers: { 'GRPC-Timeout': '1M' }, // allow for 1 minute before context deadline exceeded
        },
      },
      MetricsRequestTypes.GetAllMetricsSuccess,
      MetricsRequestTypes.GetAllMetricsFailure,
    ),
  setMetric: (body: SetMetricRequest) =>
    createGRPCAction<typeof MetricsPublic.SetMetric>()(
      {
        type: MetricsRequestTypes.SetMetricRequest,
        grpcFunction: MetricsPublic.SetMetric,
        body,
        force: true,
        readWrite: 'write',
        invalidateCacheKey: metricCacheKeysToInvalidate(body.orgId),
      },
      MetricsRequestTypes.SetMetricSuccess,
      MetricsRequestTypes.SetMetricFailure,
    ),
  markMetricViewed: (body: MarkMetricViewedRequest) =>
    createGRPCAction<typeof MetricsPublic.MarkMetricViewed, { lastViewed: timestamp }>({
      // NB: this is a hack - lastViewed timestamp is set by the server, but the server does not return the timestamp
      // Settings to Timestamp.now() is close enough
      modify: () => ({ lastViewed: Timestamp.now() }),
    })(
      {
        type: MetricsRequestTypes.MarkViewedRequest,
        grpcFunction: MetricsPublic.MarkMetricViewed,
        body,
        force: true,
        readWrite: 'write',
      },
      MetricsRequestTypes.MarkViewedSuccess,
      MetricsRequestTypes.MarkViewedFailure,
    ),
  starMetric: (body: StarMetricRequest) =>
    createGRPCAction<typeof MetricsPublic.StarMetric>()(
      {
        type: MetricsRequestTypes.StarMetricRequest,
        grpcFunction: MetricsPublic.StarMetric,
        body,
        force: true,
        invalidateCacheKey: metricCacheKeysToInvalidate(body.orgId),
        readWrite: 'write',
      },
      MetricsRequestTypes.StarMetricSuccess,
      MetricsRequestTypes.StarMetricFailure,
    ),
  archiveMetric: (body: ArchiveMetricRequest) =>
    createGRPCAction<typeof MetricsPublic.ArchiveMetric>()(
      {
        type: MetricsRequestTypes.ArchiveMetricRequest,
        grpcFunction: MetricsPublic.ArchiveMetric,
        body,
        force: true,
        invalidateCacheKey: metricCacheKeysToInvalidate(body.orgId),
        readWrite: 'write',
      },
      MetricsRequestTypes.ArchiveMetricSuccess,
      MetricsRequestTypes.ArchiveMetricFailure,
    ),
  createUnnamedMetric: (body: CreateUnnamedMetricRequest) =>
    createGRPCAction<typeof MetricsPublic.CreateUnnamedMetric>()(
      {
        type: MetricsRequestTypes.CreateUnnamedMetricRequest,
        grpcFunction: MetricsPublic.CreateUnnamedMetric,
        body,
        readWrite: 'write',
      },
      MetricsRequestTypes.CreateUnnamedMetricSuccess,
      MetricsRequestTypes.CreateUnnamedMetricFailure,
    ),
  getUnnamedMetric: (body: GetUnnamedMetricRequest) =>
    createGRPCAction<typeof MetricsPublic.GetUnnamedMetric>()(
      {
        type: MetricsRequestTypes.GetUnnamedMetricRequest,
        grpcFunction: MetricsPublic.GetUnnamedMetric,
        body,
        readWrite: 'read',
      },
      MetricsRequestTypes.GetUnnamedMetricSuccess,
      MetricsRequestTypes.GetUnnamedMetricFailure,
    ),
  computeMetric: (
    body: ComputeMetricRequest,
    searchCacheKey: SearchCacheKey,
    segmentCacheKey: number,
    force = false,
    uid?: UID,
  ) =>
    createGRPCAction<typeof AppBackendPublic.ComputeMetric, ComputeMetricResponseWithId[]>({
      // this gives metrics data unique and stable IDs that are used as keys,
      // necessary for providing LabelGroup keys
      modify: (rsp: ComputeMetricResponse): ComputeMetricResponseWithId[] =>
        rsp.responses
          .map(r =>
            r.response
              ? computeMetricModify(r.response, r.isTimeRangeComparison, rsp.cacheOption)
              : null,
          )
          .filter(isNonNull),
      cacheKey: url => {
        // we want non refresh and refresh request to share the same key (refreshes force past the cache anyways)
        const { isRefresh, ...cacheKeyBody } = body;
        return `${url}:${hashObject(cacheKeyBody)}:${hashObject(
          searchCacheKey,
        )}:${segmentCacheKey}`;
      },
      uid,
    })(
      {
        type: MetricsRequestTypes.ComputeMetricRequest,
        grpcFunction: AppBackendPublic.ComputeMetric,
        body,
        settings: {
          headers: {
            'GRPC-Timeout': '1M', // allow for 1 minute before context deadline exceeded
            'X-css-as-elements': 'true',
          },
        },
        readWrite: 'write',
        force,
      },
      MetricsRequestTypes.ComputeMetricSuccess,
      MetricsRequestTypes.ComputeMetricFailure,
    ),
  // alexa.computeMetric is now marked legacy, in favor of dx-objects.computeMetric
  legacyComputeMetric: (
    body: LegacyMetricsRequestBody,
    searchCacheKey: SearchCacheKey,
    batchOptions?: BatchOptions,
    force = false,
    uid?: UID,
  ) => {
    const reqBody = metricsRequestBodyToLegacyComputeMetricRequest(body);
    return createGRPCAction<
      typeof LegacyMetricsServicePublic.ComputeMetric,
      ComputeMetricResponseWithId
    >({
      // this gives metrics data unique and stable IDs that are used as keys,
      // necessary for providing LabelGroup keys
      modify: (rsp: AlexaComputeMetricResponse): ComputeMetricResponseWithId =>
        computeMetricModify(rsp, false, null),
      cacheKey: url => `${url}:${hashObject(reqBody)}:${hashObject(searchCacheKey)}`,
      uid,
    })(
      {
        type: MetricsRequestTypes.LegacyComputeMetricRequest,
        grpcFunction: LegacyMetricsServicePublic.ComputeMetric,
        body: reqBody,
        settings: {
          headers: {
            'GRPC-Timeout': '1M', // allow for 1 minute before context deadline exceeded
            'X-css-as-elements': 'true',
          },
          batchOptions,
        },
        readWrite: 'write',
        force,
      },
      MetricsRequestTypes.LegacyComputeMetricSuccess,
      MetricsRequestTypes.LegacyComputeMetricFailure,
    );
  },
  getAccess: (body: CanAccessRequest) =>
    createGRPCAction<typeof MetricsPublic.GetMetricsAccess>()(
      {
        type: MetricsRequestTypes.MetricsAccessRequest,
        grpcFunction: MetricsPublic.GetMetricsAccess,
        body,
        readWrite: 'read',
      },
      MetricsRequestTypes.MetricsAccessSuccess,
      MetricsRequestTypes.MetricsAccessFailure,
    ),
};

export const MetricsActions = createAppCacheActions(
  MetricsRequestTypes.MetricsAppCacheIsLoading,
  MetricsRequestTypes.MetricsAppCacheLoadFailed,
  MetricsRequestTypes.MetricsAppCacheRefetching,
  MetricsRequestTypes.MetricsAppCacheHasData,
  MetricsRequestTypes.MetricsAppCacheFresh,
  MetricsRequestTypes.MetricsAppCacheSwap,
  MetricsRequestTypes.MetricsAppCacheClear,
)<ComputeMetricResponseWithId[]>();
export type MetricsActions =
  | ActionType<typeof MetricsActions>
  | RequestActionType<typeof MetricsRequestActions>;

export interface LegacyMetricsPayload {
  body: LegacyMetricsRequestBody;
  flags: Flags;
  searchCacheKey: SearchCacheKey;
  cacheOptions: QueryCacheOptions;
}
export interface MetricsPayload {
  body: MetricsRequestBody;
  flags: Flags;
  searchCacheKey: SearchCacheKey;
  segmentCacheKey: number;
}

export const MetricsAsyncActions = {
  appCache: (uid: UID, forceSwap?: boolean) => ({
    action: createAppCacheRequestAction<ComputeMetricResponseWithId[], MetricsPayload>()({
      query: async (
        dispatch: RequestDispatch<MetricsActions>,
        payload: MetricsPayload,
        isRefresh: boolean,
      ) => {
        const data = await dispatch(
          MetricsRequestActions.computeMetric(
            {
              ...{ ...payload.body, isRefresh },
              timezone: jstz.determine().name(),
            },
            payload.searchCacheKey,
            payload.segmentCacheKey,
            isRefresh, // if user specifically requested a refresh, force the request action
            isRefresh ? undefined : uid, // if it's a refresh, don't send the UID
          ),
        );
        return data;
      },
      loading: MetricsActions.appCacheIsLoading,
      failed: MetricsActions.appCacheLoadFailed,
      refetching: MetricsActions.appCacheIsRefetching,
      hasData: MetricsActions.appCacheHasData,
      fresh: MetricsActions.appCacheFresh,
      isDataFresh: (_: MetricsPayload, datas: ComputeMetricResponseWithId[]) => {
        const freshData = datas.filter(data =>
          isDataFresh(data.cacheOptions, Timestamp.mustDate(data.computedAt), new Date()),
        );
        // check all data is fresh
        return freshData.length == datas.length;
      },
      isSimiliar: (
        newData: ComputeMetricResponseWithId[],
        oldData: ComputeMetricResponseWithId[],
      ) => {
        if (oldData.length !== newData.length) return false;
        for (let i = 0; i < oldData.length; i++) {
          const od = oldData[i];
          const nd = newData[i];
          if (!od || !nd) return false;
          if (!computeMetricIsSimilar(nd, od)) return false;
        }
        return true;
      },
      forceSwap,
    }),
    cancelSingle: (dispatch: AppDispatch, uid: UID) => {
      dispatch(createRequestCancellationAction(uid));
    },
  }),
};

const computeMetricModify = (
  rsp: AlexaComputeMetricResponse,
  isPast: boolean,
  cacheOptions: QueryCacheOptions | null,
): ComputeMetricResponseWithId =>
  rsp.data
    ? {
        ...rsp,
        data: {
          ...rsp.data,
          table: rsp.data.table.map(dataRow => ({ ...dataRow, id: UID() })),
        },
        isPast,
        cacheOptions,
      }
    : { ...rsp, data: null, isPast, cacheOptions };

const computeMetricIsSimilar = (
  newData: ComputeMetricResponseWithId,
  oldData: ComputeMetricResponseWithId,
) => {
  if (!oldData.data || !newData.data) return false;
  return equalArrays(oldData.data.table, newData.data.table);
};
